import React, { useState, useEffect } from "react";
import MyAccountSideBar from "./MyAccountSideBar";
import AddAddressModal from "./AddAddressModal";
import {
  getAddresses,
  updateAddress,
  disableAddress,
} from "../services/authenticatedRequests";
import { toast } from "react-toastify";
import LoadingComponent from "./LoadingComponent";
import NoResultsComponent from "./NoResultsComponent";

const AddressBook = () => {
  document.title = "Address Book | Bigmall";
  const [active, setActive] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [defaultShippingId, setDefaultShippingId] = useState(null);
  const [defaultBillingId, setDefaultBillingId] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // Ad

  // Toggle sidebar for mobile view
  const sidebarController = () => {
    setActive(!active);
  };

  // Fetch addresses from backend when component mounts
  useEffect(() => {

    const fetchAddresses = async () => {
      setIsLoading(true);
      try {
        const response = await getAddresses();
        if (response && response.addresses) {
          setAddresses(response.addresses);
          if (response.defaultShippingId) {
            setDefaultShippingId(response.defaultShippingId);
          }
          if (response.defaultBillingId) {
            setDefaultBillingId(response.defaultBillingId);
          }
        }
      } catch (error) {
        console.error("Error fetching addresses:", error.message);
      }
      finally {
        setIsLoading(false);
      }
    };

    fetchAddresses();
  }, []);

  // Handle Adding New Address: expects the saved address from the backend
  const handleAddAddress = (newAddress) => {
    setAddresses((prevAddresses) => [...prevAddresses, newAddress]);
    setIsModalOpen(false);
  };

  // Handle updating shipping address
  const handleShippingChange = async (e) => {
    const selectedAddressId = e.target.value;
    setDefaultShippingId(selectedAddressId);
    try {
      await updateAddress({ id: selectedAddressId, addressType: "shipping" });
      toast.success("Default shipping address updated successfully.");
    } catch (error) {
      toast.error("Failed to update default shipping address.");
    }
  };

  // Handle updating billing address
  const handleBillingChange = async (e) => {
    const selectedAddressId = e.target.value;
    setDefaultBillingId(selectedAddressId);
    try {
      await updateAddress({ id: selectedAddressId, addressType: "billing" });
      toast.success("Default billing address updated successfully.");
    } catch (error) {
      toast.error("Failed to update default billing address.");
    }
  };

  // Handle disabling (deleting) an address
  const handleDisableAddress = async (addressId) => {
    try {
      await disableAddress(addressId, true);
      setAddresses(addresses.filter((address) => address.id !== addressId));
      toast.success("Address deleted successfully.");
    } catch (error) {
      toast.error("Failed to delete address.");
    }
  };
  // 1. Grab the selected objects
const selectedShipping = addresses.find(a => a.id === defaultShippingId);   // :contentReference[oaicite:0]{index=0}
const selectedBilling  = addresses.find(a => a.id === defaultBillingId);    // :contentReference[oaicite:1]{index=1}

// 2. Filter out those two from the main list
const others = addresses.filter(
  a => a.id !== defaultShippingId && a.id !== defaultBillingId             // :contentReference[oaicite:2]{index=2}
);

// 3. Build a new array, shipping first, then billing (if distinct), then the rest
const sortedAddresses = [
  ...(selectedShipping ? [selectedShipping] : []),
  ...(selectedBilling && selectedBilling.id !== defaultShippingId
      ? [selectedBilling]
      : []),
  ...others                                                              // :contentReference[oaicite:3]{index=3}
];


  return (
    <section className="vendor-two-details py-20">
      <div
        className={`${active ? "show" : ""}`}
        onClick={sidebarController}
      ></div>
      <div className="container container-lg">
        <div className="vendor-two-details-wrapper d-flex flex-wrap align-items-start gap-24">
          {/* Sidebar */}
          <MyAccountSideBar
            active={active}
            sidebarController={sidebarController}
          />

          {/* Main Content */}
          <div className="col-12 col-lg-9">
          <div className="vendor-two-details__contents flex-grow-1">
            <div className="bg-white rounded-8 p-24">
              <div className="d-flex justify-content-between align-items-center mb-24">
                <h4 className="mb-0">My Address Book</h4>
                <div className="d-flex flex-row gap-16">
                  <button
                    className="btn btn-main btn-sm"
                    onClick={() => setIsModalOpen(true)}
                  >
                    Add New Address
                  </button>
                  <button
                    onClick={sidebarController}
                    type="button"
                    className="shop-sidebar__toggle d-lg-none d-flex align-items-center justify-content-center w-40 h-40 border border-gray-200 rounded-circle hover-bg-gray-50"
                  >
                    <i className="ph ph-list" />
                  </button>
                </div>
              </div>

              {isLoading ? (
                <LoadingComponent message="Loading addresses..." />
            ) : addresses.length === 0 ? (
                <NoResultsComponent message="No addresses found." />
              ) : (
                <div className="table-responsive ">
                  
                  <table
                    className="table table-sm small"
                    style={{
                      borderCollapse: "separate",
                      borderSpacing: "0 0.5rem",
                    }}
                  >
                    <thead>
                      <tr>
                        <th className="px-2 py-2 text-start text-sm">Full Name</th>
                        <th className="px-2 py-2 text-start text-sm">Address</th>
                        <th className="px-2 py-2 text-start text-sm">Postcode</th>
                        <th className="px-2 py-2 text-start text-sm">Phone</th>
                        <th className="px-2 py-2 text-center text-sm">
                          Default Shipping
                        </th>
                        <th className="px-2 py-2 text-center text-sm">
                          Default Billing
                        </th>
                        <th className="px-2 py-2 text-center text-sm">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedAddresses.map((address) => (
                        <tr key={address.id}>
                          <td className="px-2 py-2 text-start text-sm">
                            {address.firstName} {address.lastName}
                          </td>
                          <td className="px-2 py-2 text-start text-sm">
                            <div
                              style={{
                                maxWidth: "200px",
                                wordWrap: "break-word",
                              }}
                            >
                              {address.company && (
                                <p className="mb-0">{address.company}</p>
                              )}
                              <p className="mb-0">
                                {address.address1},<br />
                                {address.city}, {address.state},<br />
                                {address.country}
                              </p>
                              <p className="mb-0">{address.email}</p>
                            </div>
                          </td>

                          <td className="px-2 py-2 text-start text-sm">
                            {address.postCode}
                          </td>
                          <td className="px-2 py-2 text-start text-sm">
                            {address.phone}
                          </td>
                          <td className="px-2 py-2 text-center text-sm">
                            <input
                              type="radio"
                              name="defaultShipping"
                              value={address.id}
                              checked={defaultShippingId === address.id}
                              onChange={handleShippingChange}
                            />
                          </td>
                          <td className="px-2 py-2 text-center text-sm">
                            <input
                              type="radio"
                              name="defaultBilling"
                              value={address.id}
                              checked={defaultBillingId === address.id}
                              onChange={handleBillingChange}
                            />
                          </td>
                          <td className="px-2 py-2 text-center text-sm">
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleDisableAddress(address.id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* Add Address Modal */}
      <AddAddressModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleAddAddress}
      />
    </section>
  );
};

export default AddressBook;
